import { z } from 'zod';

import {
  auditFieldsSchema,
  entityIdSchema,
  isoDateSchema,
  isoDateTimeSchema,
  moneySchema,
  pageMetaSchema,
} from './common.js';
import { customerInputSchema } from './customers.js';
import {
  appliedPromoCodeSchema,
  commercialDiscountSchema,
  priceLineInputSchema,
  priceLineSchema,
} from './pricing.js';
import { reservationParticipantInputSchema } from './reservations.js';

export const quoteStatuses = [
  'draft',
  'validated',
  'optioned',
  'converted',
  'expired',
  'cancelled',
  'sent',
  'accepted',
  'rejected',
  'pending',
] as const;

export type QuoteStatus = (typeof quoteStatuses)[number];

export const quoteGeneralWritableStatuses = [
  'draft',
  'sent',
  'pending',
  'accepted',
  'rejected',
  'validated',
] as const;

export const quoteGeneralTransitionMatrix = {
  draft: ['draft', 'sent', 'pending'],
  sent: ['sent', 'pending', 'accepted', 'rejected'],
  pending: ['pending', 'sent', 'accepted', 'rejected'],
  accepted: ['accepted'],
  rejected: ['rejected'],
  validated: ['validated'],
  optioned: [],
  converted: [],
  cancelled: [],
  expired: [],
} as const satisfies Readonly<Record<QuoteStatus, readonly QuoteStatus[]>>;

export const quoteValidationSourceStatuses = ['draft', 'sent', 'pending', 'accepted'] as const;

export const quoteStatusSchema = z.enum(quoteStatuses);

export const quoteMutableStatusSchema = z.enum(quoteGeneralWritableStatuses);

export const quoteLineSchema = priceLineSchema;
export const quoteLineInputSchema = priceLineInputSchema;
export const quoteParticipantSchema = reservationParticipantInputSchema
  .omit({ id: true })
  .extend({ id: entityIdSchema });

export const quoteCustomerSelectionSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('existing'), customerId: entityIdSchema }),
  z.object({
    mode: z.literal('new'),
    profile: customerInputSchema,
    duplicateAcknowledgement: z.boolean().default(false),
  }),
]);

const quoteCoreInputSchema = z.object({
  stayId: entityIdSchema.nullable().default(null),
  status: quoteMutableStatusSchema.default('draft'),
  validUntil: isoDateSchema.nullable().default(null),
  participantCount: z.number().int().positive().max(1_000).default(1),
  participants: z.array(reservationParticipantInputSchema).max(100).default([]),
  lines: z.array(quoteLineInputSchema).min(1).max(200),
  discount: commercialDiscountSchema.default({ type: 'none', value: 0, label: '' }),
  detail: z.string().trim().max(10_000).default(''),
  idempotencyKey: z.string().trim().min(8).max(200).optional(),
});

function participantCountMatches(
  input: { readonly participantCount: number; readonly participants: readonly unknown[] },
  context: z.RefinementCtx,
): void {
  if (input.participants.length > 0 && input.participants.length !== input.participantCount) {
    context.addIssue({
      code: 'custom',
      path: ['participantCount'],
      message: 'Le nombre de participants ne correspond pas à la liste.',
    });
  }
}

const canonicalQuoteInputSchema = quoteCoreInputSchema
  .extend({ customer: quoteCustomerSelectionSchema })
  .superRefine(participantCountMatches);

const legacyQuoteInputSchema = quoteCoreInputSchema
  .extend({ customerId: entityIdSchema })
  .superRefine(participantCountMatches)
  .transform(({ customerId, ...input }) => ({
    ...input,
    customer: { mode: 'existing' as const, customerId },
  }));

export const quoteInputSchema = z.union([canonicalQuoteInputSchema, legacyQuoteInputSchema]);

export const quoteUpdateSchema = z
  .object({
    revision: z.number().int().positive(),
    customer: quoteCustomerSelectionSchema.optional(),
    customerId: entityIdSchema.optional(),
    stayId: entityIdSchema.nullable().optional(),
    status: quoteMutableStatusSchema.optional(),
    validUntil: isoDateSchema.nullable().optional(),
    participantCount: z.number().int().positive().max(1_000).optional(),
    participants: z.array(reservationParticipantInputSchema).max(100).optional(),
    lines: z.array(quoteLineInputSchema).min(1).max(200).optional(),
    discount: commercialDiscountSchema.optional(),
    detail: z.string().trim().max(10_000).optional(),
    idempotencyKey: z.string().trim().min(8).max(200).optional(),
  })
  .superRefine((input, context) => {
    if (input.customer !== undefined && input.customerId !== undefined) {
      context.addIssue({ code: 'custom', message: 'Le Client du Devis est ambigu.' });
    }
    if (
      input.participants !== undefined &&
      input.participantCount !== undefined &&
      input.participants.length > 0 &&
      input.participants.length !== input.participantCount
    ) {
      context.addIssue({
        code: 'custom',
        path: ['participantCount'],
        message: 'Le nombre de participants ne correspond pas à la liste.',
      });
    }
  });

export const quoteSchema = z
  .object({
    id: entityIdSchema,
    publicRef: z.string().trim().min(1).max(120),
    customerId: entityIdSchema,
    customerName: z.string().trim().min(1).max(240),
    customerCompany: z.string().trim().max(240),
    stayId: entityIdSchema.nullable(),
    stayReference: z.string().trim().max(160),
    status: quoteStatusSchema,
    validUntil: isoDateSchema.nullable(),
    participantCount: z.number().int().positive(),
    participants: z.array(quoteParticipantSchema).max(100),
    lines: z.array(quoteLineSchema).min(1).max(200),
    discount: commercialDiscountSchema,
    appliedPromoCodes: z.array(appliedPromoCodeSchema).default([]),
    detail: z.string(),
    totalCents: moneySchema,
    convertedReservationId: entityIdSchema.nullable(),
    validatedAt: isoDateTimeSchema.nullable(),
    validatedBy: entityIdSchema.nullable(),
  })
  .and(auditFieldsSchema);

const quoteDetailBaseSchema = z.object({
  id: entityIdSchema,
  publicRef: z.string().trim().min(1).max(120),
  customerId: entityIdSchema,
  customerName: z.string().trim().min(1).max(240),
  customerCompany: z.string().trim().max(240),
  stayId: entityIdSchema.nullable(),
  stayReference: z.string().trim().max(160),
  status: quoteStatusSchema,
  validUntil: isoDateSchema.nullable(),
  participantCount: z.number().int().positive(),
  participants: z.array(quoteParticipantSchema).max(100),
  detail: z.string(),
  convertedReservationId: entityIdSchema.nullable(),
  validatedAt: isoDateTimeSchema.nullable(),
  validatedBy: entityIdSchema.nullable(),
  createdAt: isoDateTimeSchema,
  createdBy: entityIdSchema.nullable(),
  updatedAt: isoDateTimeSchema,
  updatedBy: entityIdSchema.nullable(),
  revision: z.number().int().positive(),
});

export const quoteDetailSchema = z.discriminatedUnion('financeVisibility', [
  quoteDetailBaseSchema
    .extend({
      financeVisibility: z.literal('granted'),
      lines: z.array(quoteLineSchema).min(1).max(200),
      discount: commercialDiscountSchema,
      appliedPromoCodes: z.array(appliedPromoCodeSchema).default([]),
      totalCents: moneySchema,
    })
    .strict(),
  quoteDetailBaseSchema.extend({ financeVisibility: z.literal('omitted') }).strict(),
]);

export const quotePageSchema = z.object({
  items: z.array(quoteSchema),
  page: pageMetaSchema,
});

export const quoteValidationSchema = z.object({
  revision: z.number().int().positive(),
  idempotencyKey: z.string().trim().min(8).max(200),
});

export const quoteConversionSchema = z.object({
  revision: z.number().int().positive(),
  optionId: entityIdSchema.nullable().default(null),
  participants: z.array(reservationParticipantInputSchema).min(1).max(100).optional(),
  idempotencyKey: z.string().trim().min(8).max(200),
  redListAcknowledgement: z.boolean().default(false),
});

export const optionConversionSchema = z.object({
  revision: z.number().int().positive(),
  participants: z.array(reservationParticipantInputSchema).min(1).max(100).optional(),
  idempotencyKey: z.string().trim().min(8).max(200),
  redListAcknowledgement: z.boolean().default(false),
});

export const commercialConversionResultSchema = z.object({
  reservationId: entityIdSchema,
  quoteId: entityIdSchema.nullable(),
  optionId: entityIdSchema.nullable(),
});

export const commercialListSortSchema = z.enum(['updated_desc', 'created_desc', 'validity_asc']);
export const commercialItemKindFilterSchema = z.enum(['all', 'quote', 'option']);
export const commercialListQuerySchema = z.object({
  cursor: z.string().trim().min(1).max(1_000).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  search: z.string().trim().max(200).optional(),
  kind: commercialItemKindFilterSchema.default('all'),
  status: z.string().trim().max(40).optional(),
  dateFrom: isoDateSchema.optional(),
  dateTo: isoDateSchema.optional(),
  sort: commercialListSortSchema.default('updated_desc'),
});

export const commercialListCursorSchema = z.object({
  sort: commercialListSortSchema,
  value: z.string().min(1).max(100),
  kind: z.enum(['quote', 'option']),
  id: entityIdSchema,
});

const commercialItemBaseSchema = z.object({
  kind: z.enum(['quote', 'option']),
  id: entityIdSchema,
  quoteId: entityIdSchema.nullable(),
  optionId: entityIdSchema.nullable(),
  reference: z.string().min(1),
  customerId: entityIdSchema,
  customerName: z.string().min(1),
  customerCompany: z.string(),
  stayId: entityIdSchema.nullable(),
  stayReference: z.string(),
  status: z.string().min(1),
  relevantAt: isoDateTimeSchema,
  validityDate: isoDateSchema.nullable(),
  participantCount: z.number().int().positive(),
  revision: z.number().int().positive(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});

export const commercialItemSchema = z.discriminatedUnion('financeVisibility', [
  commercialItemBaseSchema
    .extend({ financeVisibility: z.literal('granted'), totalCents: moneySchema })
    .strict(),
  commercialItemBaseSchema.extend({ financeVisibility: z.literal('omitted') }).strict(),
]);

export const commercialKpisSchema = z.object({
  total: z.number().int().nonnegative(),
  quotes: z.number().int().nonnegative(),
  activeOptions: z.number().int().nonnegative(),
  expiredOptions: z.number().int().nonnegative(),
  converted: z.number().int().nonnegative(),
});

export const commercialPageSchema = z.object({
  items: z.array(commercialItemSchema),
  page: pageMetaSchema,
  kpis: commercialKpisSchema,
});

export const paymentMethodSchema = z.enum(['cash', 'card', 'transfer', 'cheque', 'other']);

export const paymentInputSchema = z
  .object({
    reservationId: entityIdSchema,
    amountCents: z.number().int().positive(),
    paidAt: isoDateTimeSchema.optional(),
    paidOn: isoDateSchema.optional(),
    method: paymentMethodSchema,
    reference: z.string().trim().max(200).default(''),
    idempotencyKey: z.string().trim().min(8).max(200),
  })
  .superRefine((input, context) => {
    if (input.paidAt === undefined && input.paidOn === undefined) {
      context.addIssue({ code: 'custom', message: 'La date du règlement est obligatoire.' });
    }
  })
  .transform((input) => {
    const paidAt = input.paidAt ?? `${input.paidOn ?? ''}T00:00:00.000Z`;
    return { ...input, paidAt, paidOn: input.paidOn ?? paidAt.slice(0, 10) };
  });

export const paymentSchema = z.object({
  id: entityIdSchema,
  reservationId: entityIdSchema,
  amountCents: z.number().int().positive(),
  paidAt: isoDateTimeSchema,
  paidOn: isoDateSchema,
  method: paymentMethodSchema,
  reference: z.string(),
  createdAt: isoDateTimeSchema,
  createdBy: entityIdSchema.nullable(),
});

export const paymentListQuerySchema = z.object({ reservationId: entityIdSchema });

export const debtorListSortSchema = z.enum(['outstanding_desc', 'starts_asc', 'updated_desc']);
export const debtorListQuerySchema = z.object({
  cursor: z.string().trim().min(1).max(1_000).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  search: z.string().trim().max(200).optional(),
  stayId: entityIdSchema.optional(),
  startsFrom: isoDateSchema.optional(),
  startsTo: isoDateSchema.optional(),
  sort: debtorListSortSchema.default('outstanding_desc'),
});
export const debtorListCursorSchema = z.object({
  sort: debtorListSortSchema,
  value: z.union([z.string(), z.number().int()]),
  id: entityIdSchema,
});

export const debtorSchema = z.object({
  reservationId: entityIdSchema,
  reservationRef: z.string().min(1),
  customerId: entityIdSchema,
  customerName: z.string().min(1),
  customerCompany: z.string(),
  stayId: entityIdSchema,
  stayReference: z.string().min(1),
  startsOn: isoDateSchema,
  totalCents: moneySchema,
  cashPaidCents: moneySchema,
  creditAppliedCents: moneySchema,
  giftVoucherAppliedCents: moneySchema,
  settledCents: moneySchema,
  paidCents: moneySchema,
  outstandingCents: z.number().int().positive(),
  updatedAt: isoDateTimeSchema,
});

export const debtorPageSchema = z.object({ items: z.array(debtorSchema), page: pageMetaSchema });

export const debtorDetailSchema = debtorSchema.extend({ payments: z.array(paymentSchema) });

export const financialEventSchema = z.object({
  id: entityIdSchema,
  occurredAt: isoDateTimeSchema,
  amountCents: z.number().int(),
  kind: z.enum(['charge', 'payment', 'refund', 'credit', 'voucher']),
});

export type CommercialItem = z.infer<typeof commercialItemSchema>;
export type CommercialPage = z.infer<typeof commercialPageSchema>;
export type CommercialConversionResult = z.infer<typeof commercialConversionResultSchema>;
export type CommercialListCursor = z.infer<typeof commercialListCursorSchema>;
export type CommercialListQuery = z.infer<typeof commercialListQuerySchema>;
export type Debtor = z.infer<typeof debtorSchema>;
export type DebtorDetail = z.infer<typeof debtorDetailSchema>;
export type DebtorListCursor = z.infer<typeof debtorListCursorSchema>;
export type DebtorListQuery = z.infer<typeof debtorListQuerySchema>;
export type Payment = z.infer<typeof paymentSchema>;
export type PaymentInput = z.output<typeof paymentInputSchema>;
export type Quote = z.infer<typeof quoteSchema>;
export type QuoteDetail = z.infer<typeof quoteDetailSchema>;
export type QuoteConversion = z.infer<typeof quoteConversionSchema>;
export type QuoteInput = z.output<typeof quoteInputSchema>;
export type QuoteLine = z.infer<typeof quoteLineSchema>;
export type QuoteUpdate = z.infer<typeof quoteUpdateSchema>;
